/**
 * Strings store for managing strings-original data with translations
 * 
 * This store handles caching and syncing of strings-original data for games,
 * using the GenericStore pattern for persistent caching.
 */

import { storeRegistry } from "@/rerun-manager/GenericStore";
import { apiService } from "@/lib/api-functions/api-service";
import {  translation_line } from "@/types-games";
import { objectsStore } from "./objectsStore";

/**
 * Metadata about a variable reference found in a string's source text.
 * Used for translation awareness — lets translators see what dynamic
 * content is referenced and whether it has its own translation.
 */
export interface StringVariableReference {
  /** The raw token, e.g. "{{ player.health }}" */
  raw: string;
  /** The object/type key, e.g. "player" */
  key: string;
  /** The field/element name, e.g. "health" */
  field: string;
  /** Whether the reference came from a variable/type or an object */
  sourceType: 'variable' | 'object';
  /** Whether the referenced field is itself translatable */
  isTranslatable: boolean;
  /** The string_id of the referenced field (if translatable) */
  stringId?: string;
}

// Strings item interface matching the API response
export interface StringsItem {
  id: string;
  version_id: string;
  game_id: string;
  source_text: string;
  section: string;
  context: string;
  metadata: Record<string, unknown>[];
  abbrev: string;
  status: string;
  created_at: string;
  updated_at: string;
  updated_by: string;
  created_by: string;
  translations: translation_line[];
  /** Variable references ({{ key.name }}) found in the source text */
  variable_references?: StringVariableReference[];
}

/**
 * Store for strings items, using GenericStore
 */
export const stringsStore = storeRegistry.getStore<StringsItem>({
  key: 'strings_items',
  ttlMs: 1000 * 60 * 30, // 30 minutes cache
});

/**
 * Fetch strings items for a specific game from the API and sync the store
 * @param gameId - The game ID to fetch strings items for
 * @param toast - Optional toast function for error reporting
 */
export async function fetchStringsItems(
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<StringsItem[]> {
  
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Fetch data from API
    const result = await apiService.fetchStringsItems(gameId);
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to fetch strings items');
    }

    const stringsItems = result.data;
    
    // Sync the store with the fetched data
    await stringsStore.syncFromRemote(async () => stringsItems);

    return stringsItems;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to fetch strings items';
    console.error('❌ Error in fetchStringsItems:', err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Create a new string item via API and sync the store
 * @param stringData - The string data to create
 * @param toast - Optional toast function for error reporting
 */
export async function createString(
  stringData: {
    game_id: string;
    source_text: string;
    section?: string;
    context?: string;
    metadata?: Record<string, unknown>[];
    abbrev?: string;
    status?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<StringsItem> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create string via API
    const result = await apiService.createString(stringData);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to create string item');
    }

    const newString = result.data;

    // Add the new string to the store
    const currentData = stringsStore.getData();
    const updatedData = [...currentData, newString]; // Create a new array instead of mutating
    stringsStore.setData(updatedData);

    return newString;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create string item';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Get cached strings items for a specific game
 * @param gameId - The game ID to get cached data for
 */
export function getCachedStringsItems(gameId: string): StringsItem[] {
  const allData = stringsStore.getData();
  return allData.filter(item => item.game_id === gameId);
}

/**
 * Update a string's source_text locally in the store (no API call).
 * Used to keep the stringsStore in sync when the user edits text in the
 * dialog wizard before saving. This way, edit mode and other UI components
 * see the latest text immediately.
 *
 * @param stringId - The string ID to update
 * @param sourceText - The new source text
 */
export function updateStringSourceTextLocally(stringId: string, sourceText: string): void {
  const currentData = stringsStore.getData();
  const idx = currentData.findIndex(item => item.id === stringId);
  if (idx === -1) return; // String not in store yet (e.g. first save hasn't happened)
  
  const updatedData = [...currentData];
  updatedData[idx] = { ...updatedData[idx], source_text: sourceText };
  stringsStore.setData(updatedData);
}

/**
 * Update a strings item via API and sync the store
 * @param item - The strings item data to update
 * @param toast - Optional toast function for error reporting
 */
export async function updateStringsItem(
  item: {
    id: string;
    game_id: string;
    source_text?: string;
    section?: string;
    context?: string;
    abbrev?: string;
    status?: string;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<StringsItem> {
  try {
    
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Update data via API
    const result = await apiService.updateStringItem(item);
    
    if (!result.success || !result.data) {
      throw new Error(result.error || 'Failed to update strings item');
    }

    const updatedItem = result.data;

    // Update the item in the store using the efficient updateItem method
    const updateSuccess = stringsStore.updateItem(item.id, (existingItem) => ({
      ...existingItem,
      ...updatedItem,
      // Preserve existing translations if not provided in update
      translations: updatedItem.translations || existingItem.translations
    }));
    
    if (!updateSuccess) {
      console.log('Item was unchanged, no store update needed');
    }
    
    // After successful update, find and update related glossary items
    if (item.source_text !== undefined) {
      await updateRelatedGlossaryItems(item.id, item.source_text);
    }
    
    return updatedItem;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update strings item';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Update a translation line via API
 * @param translationId - The ID of the translation to update
 * @param translationData - The updated translation data
 * @param toast - Optional toast function for error reporting
 */
export async function updateTranslationLine(
  translationId: string,
  translationData: {
    language_code: string;
    translated_text: string;
    approved: boolean;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<any> {
  console.log('🔄 stringsStore: updateTranslationLine called', {
    translationId,
    translationData,
    timestamp: new Date().toISOString(),
    stackTrace: new Error().stack?.split('\n').slice(1, 5)
  });

  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Update translation via API
    console.log('📡 stringsStore: Calling API to update translation', {
      translationId,
      translationData,
      timestamp: new Date().toISOString()
    });

    const result = await apiService.updateTranslationLine(translationId, translationData);
    
    if (!result.success || !result.data) {
      console.error('❌ stringsStore: Update API call failed', {
        result,
        translationId,
        translationData
      });
      throw new Error(result.error || 'Failed to update translation line');
    }

    const updatedTranslation = result.data;
    console.log('✅ stringsStore: Update API call successful', {
      updatedTranslation,
      originalData: translationData
    });

    // Find the string item that contains this translation
    const currentData = stringsStore.getData();
    const stringItem = currentData.find(item => 
      item.translations?.some(trans => trans.id === translationId)
    );
    
    if (stringItem) {
      // Update the translation in the store using the efficient updateItem method
      stringsStore.updateItem(stringItem.id, (existingItem) => ({
        ...existingItem,
        translations: existingItem.translations?.map(translation => 
          translation.id === translationId ? updatedTranslation : translation
        ) || []
      }));
    }

    return updatedTranslation;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to update translation line';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Create a new translation line via API
 * @param translationData - The new translation data
 * @param toast - Optional toast function for error reporting
 */
export async function createTranslationLine(
  translationData: {
    dialogue_line_id: string;
    language_code: string;
    translated_text: string;
    approved: boolean;
  },
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<any> {
  console.log('🚀 stringsStore: createTranslationLine called', {
    translationData,
    isEmptyText: !translationData.translated_text || translationData.translated_text.trim() === '',
    timestamp: new Date().toISOString(),
    stackTrace: new Error().stack?.split('\n').slice(1, 5)
  });

  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Create translation via API
    console.log('📡 stringsStore: Calling API to create translation', {
      translationData,
      timestamp: new Date().toISOString()
    });

    const result = await apiService.createTranslationLine(translationData);
    
    if (!result.success || !result.data) {
      console.error('❌ stringsStore: API call failed', {
        result,
        translationData
      });
      throw new Error(result.error || 'Failed to create translation line');
    }

    const newTranslation = result.data;
    console.log('✅ stringsStore: API call successful', {
      newTranslation,
      originalData: translationData
    });

    // Update the translation in the store using the efficient updateItem method
    console.log('🔄 stringsStore: Updating store with new translation', {
      dialogueLineId: translationData.dialogue_line_id,
      newTranslation,
      beforeUpdate: stringsStore.getData().find(item => item.id === translationData.dialogue_line_id)?.translations?.length || 0
    });
    
    stringsStore.updateItem(translationData.dialogue_line_id, (existingItem) => {
      const updatedItem = {
        ...existingItem,
        translations: [...(existingItem.translations || []), newTranslation]
      };
      
      console.log('🔄 stringsStore: Item updated', {
        itemId: existingItem.id,
        oldTranslationsCount: existingItem.translations?.length || 0,
        newTranslationsCount: updatedItem.translations.length
      });
      
      return updatedItem;
    });
    
    console.log('🔄 stringsStore: Store update completed, checking final state', {
      finalTranslationsCount: stringsStore.getData().find(item => item.id === translationData.dialogue_line_id)?.translations?.length || 0
    });

    return newTranslation;
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to create translation line';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Clear the strings cache
 */
export async function clearStringsCache(): Promise<void> {
  await stringsStore.clearCache();
} 

/**
 * Update related glossary items when a string is updated
 * @param stringId - The ID of the updated string
 * @param newText - The new text content
 */
async function updateRelatedGlossaryItems(stringId: string, newText: string): Promise<void> {
  try {
    // Import the glossary store to avoid circular dependencies
    const { objectsStore } = await import('./objectsStore');
    
    const glossaryItems = objectsStore.getData();
    
    for (const item of glossaryItems) {
      let hasChanges = false;
      const updatedSectionData = item.data.map(section => ({
        ...section,
        fields: section.fields.map(field => {
          // Handle single translatable fields (legacy format)
          if (field.string_id === stringId && field.translation) {
            hasChanges = true;
            return { ...field, value: newText };
          }
          
          // Handle multiple translatable fields (TranslatableValue[] format)
          if (field.translation && field.multiple && Array.isArray(field.value)) {
            const updatedValues = field.value.map((item: any) => {
              // Check if this is a TranslatableValue object with the matching string_id
              if (item && typeof item === 'object' && 'string_id' in item && item.string_id === stringId) {
                hasChanges = true;
                return { ...item, text: newText };
              }
              return item;
            });
            
            if (hasChanges) {
              return { ...field, value: updatedValues };
            }
          }
          
          return field;
        })
      }));
      
      if (hasChanges) {
        // Update the glossary item in the store
        const updatedItem = { ...item, data: updatedSectionData };
        const currentData = objectsStore.getData();
        const updatedData = currentData.map(gi => 
          gi.id === item.id ? updatedItem : gi
        );
        objectsStore.setData(updatedData);
        
        console.log(`Updated glossary item ${item.id} with new string content for string ${stringId}`);
      }
    }
  } catch (error) {
    console.error('Error updating related glossary items:', error);
  }
}

/**
 * Clean up related glossary items when a string is deleted
 * @param stringId - The ID of the deleted string
 */
async function cleanupRelatedGlossaryItems(stringId: string): Promise<void> {
  try {
    // Import the glossary store to avoid circular dependencies
    const { objectsStore } = await import('./objectsStore');
    
    const glossaryItems = objectsStore.getData();
    
    for (const item of glossaryItems) {
      let hasChanges = false;
      const updatedSectionData = item.data.map(section => ({
        ...section,
        fields: section.fields.map(field => {
          // Handle single translatable fields (legacy format)
          if (field.string_id === stringId) {
            hasChanges = true;
            return { ...field, string_id: undefined, translation: false };
          }
          
          // Handle multiple translatable fields (TranslatableValue[] format)
          if (field.translation && field.multiple && Array.isArray(field.value)) {
            const updatedValues = field.value.map((item: any) => {
              // Check if this is a TranslatableValue object with the matching string_id
              if (item && typeof item === 'object' && 'string_id' in item && item.string_id === stringId) {
                hasChanges = true;
                return { ...item, string_id: undefined };
              }
              return item;
            });
            
            if (hasChanges) {
              return { ...field, value: updatedValues };
            }
          }
          
          return field;
        })
      }));
      
      if (hasChanges) {
        // Update the glossary item in the store
        const updatedItem = { ...item, data: updatedSectionData };
        const currentData = objectsStore.getData();
        const updatedData = currentData.map(gi => 
          gi.id === item.id ? updatedItem : gi
        );
        objectsStore.setData(updatedData);
        
        console.log(`Cleaned up glossary item ${item.id} after string ${stringId} deletion`);
      }
    }
  } catch (error) {
    console.error('Error cleaning up related glossary items:', error);
  }
}

/**
 * Delete a string item via API and sync the store
 * @param id - The string ID to delete
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function deleteString(
  id: string,
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<void> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Delete data via API
    const result = await apiService.deleteStringItem(id, gameId);
    
    if (!result.success) {
      throw new Error(result.error || 'Failed to delete string item');
    }

    // Remove the item from the store using the efficient updateItem method
    const currentData = stringsStore.getData();
    const updatedData = currentData.filter(item => item.id !== id);
    
    // Only update the store if the data actually changed
    const hasChanges = currentData.length !== updatedData.length;
    if (hasChanges) {
      stringsStore.setData(updatedData);
    }

    // After successful deletion, clean up related glossary items
    await cleanupRelatedGlossaryItems(id);

  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to delete string item';
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Mark a string as deprecated
 * @param id - The string ID to mark as deprecated
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function markStringAsDeprecated(
  id: string,
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<void> {
  try {
    // Get the auth token
    const token = await apiService.getToken();
    if (!token) {
      throw new Error('No authentication token available');
    }

    // Update the string status to deprecated
    await updateStringsItem({
      id,
      game_id: gameId,
      status: 'deprecated'
    }, toast);

    console.log(`Successfully marked string ${id} as deprecated`);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to mark string as deprecated';
    console.error('❌ Error in markStringAsDeprecated:', err);
    if (toast) {
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
    throw new Error(message);
  }
}

/**
 * Mark multiple strings as deprecated
 * @param stringIds - Array of string IDs to mark as deprecated
 * @param gameId - The game ID
 * @param toast - Optional toast function for error reporting
 */
export async function markStringsAsDeprecated(
  stringIds: string[],
  gameId: string,
  toast?: (props: { title?: React.ReactNode; description?: React.ReactNode; variant?: string }) => void
): Promise<void> {
  try {
    console.log(`Marking ${stringIds.length} strings as deprecated`);
    
    // Mark each string as deprecated
    for (const stringId of stringIds) {
      await markStringAsDeprecated(stringId, gameId, toast);
    }
    
    console.log(`Successfully marked ${stringIds.length} strings as deprecated`);
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Failed to mark strings as deprecated';
    console.error('❌ Error in markStringsAsDeprecated:', err);
    throw new Error(message);
  }
} 